import {
  ActionLayout,
  Button,
  ExternalLink,
  Grid,
  TextFieldController,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { Control, FormState, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { ACHNumbers, ConnectACHFormValues } from './types'
import { useConnectStripeACHForm } from './useConnectACHForm'

type ConnectStripeACHFormProps = {
  clientSecret: string
  onError: () => void
  onSuccess: (achNumbers: ACHNumbers) => void
  children?: React.ReactNode
}

export const ConnectStripeACHForm = ({
  clientSecret,
  onSuccess,
  onError,
  children,
}: ConnectStripeACHFormProps) => {
  const { control, onSubmit, formState } = useConnectStripeACHForm(clientSecret, {
    onSuccess,
    onError,
  })

  if (!onSubmit) return null

  return (
    <ConnectACHForm control={control} onSubmit={onSubmit} formState={formState}>
      {children}
    </ConnectACHForm>
  )
}

type ConnectACHPaymentSourceFormProps = {
  onSuccess: (achNumbers: ACHNumbers) => void
  children?: React.ReactNode
}
export const ConnectACHPaymentSourceForm = ({ onSuccess, children }: ConnectACHPaymentSourceFormProps) => {
  const { control, handleSubmit, formState } = useForm<ConnectACHFormValues>()
  const onSubmit = handleSubmit(onSuccess)
  return (
    <ConnectACHForm control={control} onSubmit={onSubmit} formState={formState}>
      {children}
    </ConnectACHForm>
  )
}

type ConnectACHFormProps = {
  control: Control<ConnectACHFormValues>
  formState?: FormState<ConnectACHFormValues>
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>
  children?: React.ReactNode
}
const ConnectACHForm = ({ control, formState, onSubmit, children }: ConnectACHFormProps) => {
  const intl = useIntl()
  const required = intl.formatMessage({ id: 'common.validation.required' })

  // ACH test numbers
  // 000123456789	110000000	The automatic bank account validation succeeds.
  // 000111111114	110000000	The automatic bank account validation fails.

  return (
    <form onSubmit={onSubmit}>
      <Grid container spacing={1} alignItems="end" sx={{ mb: 2 }}>
        <Grid size={6}>
          <TextFieldController
            control={control}
            name="routingNumber"
            label="Routing number"
            required
            fullWidth
            rules={{ required }}
          />
        </Grid>
        <Grid size={6}>
          <TextFieldController
            control={control}
            name="accountNumber"
            label="Account number"
            required
            fullWidth
            rules={{ required }}
          />
        </Grid>
      </Grid>
      {formState?.errors.root && (
        <>
          <Typography variant="body2" color="error" gutterBottom>
            We were unable to verify your account information. Please check you entered the numbers correctly.
          </Typography>
          <Typography variant="body2" gutterBottom>
            If this problem persists, please reach out to us for support at{' '}
            <ExternalLink href="mailto:support@thisisalice.com">
              <Typography variant="body2" color="inherit" component="span">
                support@thisisalice.com
              </Typography>
            </ExternalLink>
          </Typography>
        </>
      )}

      {children}

      <ActionLayout
        disablePadding
        primary={
          <Button variant="contained" type="submit" fullWidth>
            <FormattedMessage id="common.confirm" />
          </Button>
        }
      />
    </form>
  )
}
